import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Download, FileImage, LogOut, ShieldCheck } from "lucide-react";
import { toPng } from "html-to-image";
import * as XLSX from "xlsx";
import { useRef } from "react";
import { useAuth } from "../auth/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export const Route = createFileRoute("/admin")({
  component: AdminPage,
});

type Summary = { activeUsers: number; auditEvents30Days: number };

function AdminPage() {
  const auth = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const summary = useQuery({
    queryKey: ["admin-summary"],
    queryFn: async () => {
      const response = await auth.authFetch("/api/admin/summary");
      if (!response.ok) throw new Error("Could not load administration data.");
      return (await response.json()) as Summary;
    },
    enabled: Boolean(auth.user?.roles.includes("admin")),
  });

  if (!auth.user) {
    return (
      <main className="auth-page">
        <section className="auth-card">
          <h1>Administrator sign-in required</h1>
          <Link className="admin-primary-link" to="/login">
            Open sign in
          </Link>
          <Link to="/">Back to the books</Link>
        </section>
      </main>
    );
  }

  if (!auth.user.roles.includes("admin")) {
    return (
      <main className="auth-page">
        <section className="auth-card">
          <h1>Administrator permission required</h1>
          <p>This account cannot open the private dashboard.</p>
          <Link to="/">Back to the books</Link>
        </section>
      </main>
    );
  }

  const data = summary.data || { activeUsers: 0, auditEvents30Days: 0 };

  function exportWorkbook() {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet([
      { Metric: "Active users", Value: data.activeUsers },
      { Metric: "Audit events (30 days)", Value: data.auditEvents30Days },
    ]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");
    XLSX.writeFile(workbook, "kirtan-finder-summary.xlsx");
  }

  async function saveImage() {
    if (!panelRef.current) return;
    const url = await toPng(panelRef.current, { pixelRatio: 2 });
    const link = document.createElement("a");
    link.download = "kirtan-finder-dashboard.png";
    link.href = url;
    link.click();
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <span className="eyebrow">Private dashboard</span>
          <h1>Kirtan Finder administration</h1>
        </div>
        <button onClick={() => void auth.logout()}>
          <LogOut aria-hidden="true" /> Sign out
        </button>
      </header>

      <section className="admin-panel" ref={panelRef}>
        <div className="admin-trust">
          <ShieldCheck aria-hidden="true" />
          <div>
            <strong>Protected access</strong>
            <span>{auth.user.email}</span>
          </div>
        </div>
        {summary.isError && <div className="auth-error">Could not load the summary.</div>}
        <div className="admin-metrics">
          <article>
            <span>Active users</span>
            <strong>{data.activeUsers}</strong>
          </article>
          <article>
            <span>Audit events · 30 days</span>
            <strong>{data.auditEvents30Days}</strong>
          </article>
        </div>
        <div className="admin-chart">
          <Bar
            data={{
              labels: ["Active users", "Recent events"],
              datasets: [
                {
                  label: "Count",
                  data: [data.activeUsers, data.auditEvents30Days],
                  backgroundColor: ["#b97a18", "#6e8f9f"],
                },
              ],
            }}
            options={{ responsive: true, maintainAspectRatio: false }}
          />
        </div>
      </section>

      <div className="admin-actions">
        <button onClick={exportWorkbook}>
          <Download aria-hidden="true" /> Export Excel
        </button>
        <button onClick={() => void saveImage()}>
          <FileImage aria-hidden="true" /> Save image
        </button>
        <Link to="/">Open Kirtan Finder</Link>
      </div>
    </main>
  );
}
